import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

interface TopLoaderProps {
  isLoading?: boolean;
  progress?: number;
}

export const TopLoader: React.FC<TopLoaderProps> = ({ 
  isLoading: externalLoading, 
  progress: externalProgress 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const location = useLocation();

  // Reset on route change
  useEffect(() => {
    setIsVisible(true);
    setProgress(10);
    
    const timer1 = setTimeout(() => setProgress(50), 100);
    const timer2 = setTimeout(() => setProgress(80), 300);
    const timer3 = setTimeout(() => {
      setProgress(100);
      setTimeout(() => setIsVisible(false), 200);
    }, 600);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [location.pathname]);

  // Handle external loading state
  useEffect(() => {
    if (externalLoading !== undefined) {
      setIsVisible(externalLoading);
      if (externalLoading) {
        setProgress(externalProgress || 30);
      } else {
        setProgress(100);
        setTimeout(() => setIsVisible(false), 200);
      }
    }
  }, [externalLoading, externalProgress]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed top-0 left-0 right-0 z-[9999] h-1 bg-primary/20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="h-full bg-primary"
            initial={{ width: '0%' }}
            animate={{ width: `${progress}%` }}
            transition={{
              duration: progress === 100 ? 0.2 : 0.8,
              ease: progress === 100 ? 'easeOut' : [0.4, 0.0, 0.2, 1],
            }}
          />
          
          {/* Shimmer effect */}
          <motion.div
            className="absolute top-0 left-0 h-full w-32 bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{
              x: ['-128px', '100vw'],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Hook to control loader programmatically
export const useTopLoader = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const start = (initialProgress = 30) => {
    setIsLoading(true);
    setProgress(initialProgress);
  };

  const update = (newProgress: number) => {
    setProgress(Math.min(newProgress, 95));
  };

  const finish = () => {
    setProgress(100);
    setIsLoading(false);
  };

  return { start, update, finish, isLoading, progress };
};